<template>
  <div class="container">

    <h1 class="se-bp-title">Employment Details: {{ getWageEmploymentLabel(employmentIdx) }}</h1>

    <div class="se-outlined-box" v-if="currentEmployment">
      <div class="row mt-3">
        <div class="col-md-6">
          <fg-input label="Employer Name" type="text" :readonly="isLocked" v-model="employerName" :showHelpIcon="false"></fg-input>
        </div>
        <div class="col-md-6">
          <label class="bp-form-label">Employment Status</label>
          <div class="se-toggle-group">
            <button type="button" class="se-toggle-pill" v-for="opt in employmentStatusOptions" :key="opt.value"
                    :class="{'se-toggle-pill--active': employmentStatus === opt.value}"
                    :disabled="isLocked || opt.value === 'previous'" @click="employmentStatus = opt.value">{{ opt.label }}</button>
          </div>
        </div>
      </div>

      <div class="row mt-3 se-bold-input-text">
        <div class="col-md-6">
          <label class="bp-form-label">Employee Status</label>
          <el-select size="large" v-model="employeeStatus" :disabled="isLocked" clearable placeholder="Select">
            <el-option v-for="opt in employeeStatusOptions" :key="opt.value" :label="opt.label" :value="opt.value" :disabled="opt.disabled"/>
          </el-select>
        </div>
        <div class="col-md-3">
          <label class="bp-form-label">Employment Start Date</label>
          <el-date-picker size="large" v-model="employmentStartDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
        </div>
        <div class="col-md-3" v-if="employmentStatus === 'previous'">
          <label class="bp-form-label">Employment End Date</label>
          <el-date-picker size="large" v-model="employmentEndDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-md-6 mt-3">
          <label class="bp-form-label d-block">Employed on a Contract Basis</label>
          <radio :label="true" :disabled="isLocked" v-model="contractBasisIndicator" class="d-inline-block mr-4">Yes</radio>
          <radio :label="false" :disabled="isLocked" v-model="contractBasisIndicator" class="d-inline-block">No</radio>
        </div>
        <div class="col-md-6 mt-3">
          <label class="bp-form-label d-block">Employed by a family member or party to the transaction</label>
          <radio :label="true" :disabled="isLocked" v-model="specialRelationshipIndicator" class="d-inline-block mr-4">Yes</radio>
          <radio :label="false" :disabled="isLocked" v-model="specialRelationshipIndicator" class="d-inline-block">No</radio>
        </div>
        <div class="col-md-6 mt-3">
          <label class="bp-form-label d-block">Paid in foreign currency</label>
          <radio :label="true" :disabled="isLocked" v-model="foreignIncomeIndicator" class="d-inline-block mr-4">Yes</radio>
          <radio :label="false" :disabled="isLocked" v-model="foreignIncomeIndicator" class="d-inline-block">No</radio>
        </div>
        <div class="col-md-6 mt-3">
          <label class="bp-form-label d-block">Paid in virtual currency</label>
          <radio :label="true" :disabled="isLocked" v-model="virtualCurrencyIndicator" class="d-inline-block mr-4">Yes</radio>
          <radio :label="false" :disabled="isLocked" v-model="virtualCurrencyIndicator" class="d-inline-block">No</radio>
        </div>
      </div>
    </div>

    <se-buttons
      :display-previous="true"
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {ElSelect, ElOption, ElDatePicker} from 'element-plus'
import {Radio} from 'src/components/index'
import Swal from 'sweetalert2/dist/sweetalert2'
import SeButtons from './SeButtons.vue'
import wageFormMixin from '@/mixins/WageFormMixin'
import Enums from 'src/model/enums'
import {SET_EXTRA_DATA, SET_WAGE_EMPLOYMENT_DATA} from '@/store/actions/se'
import {mapGetters} from 'vuex'

export default defineComponent({
  mixins: [wageFormMixin],

  components: {
    Radio,
    SeButtons,
    [ElSelect.name]: ElSelect,
    [ElOption.name]: ElOption,
    [ElDatePicker.name]: ElDatePicker
  },

  data() {
    return {
      employeeStatusOptions: [
        {value: Enums.SeEmployeeStatusType.active, label: Enums.SeEmployeeStatusTypeLabel.active, disabled: false},
        {value: Enums.SeEmployeeStatusType.inactive, label: 'On Leave', disabled: false}
      ],
      employmentStatusOptions: Object.keys(Enums.SeEmploymentStatusType).map(key => ({
        value: Enums.SeEmploymentStatusType[key],
        label: Enums.SeEmploymentStatusTypeLabel[key]
      }))
    }
  },

  watch: {
    // For the first employment, keep its start date in sync with the Employment Start Date
    // collected on the Business Length page.
    currentEmployment: {
      immediate: true,
      handler() {
        this.maybeSyncEmploymentStartDate()
      }
    }
  },

  computed: {
    ...mapGetters(['getEmploymentMonthsCount']),

    employerName: {
      get() { return this.currentEmployment?.employerName },
      set(v) { this.setEmploymentField('employerName', v) }
    },
    employeeStatus: {
      get() { return this.currentEmployment?.employeeStatus },
      set(v) { this.setEmploymentField('employeeStatus', v) }
    },
    employmentStartDate: {
      get() { return this.currentEmployment?.employmentStartDate },
      set(v) {
        this.setEmploymentField('employmentStartDate', v)
        // Keep the Business Length page's Employment Start Date in sync (first employment only).
        if (this.employmentIdx === 0) {
          this.$store.commit(SET_EXTRA_DATA, {employmentStartDateProvided: v})
        }
      }
    },
    employmentStatus: {
      get() { return this.currentEmployment?.employmentStatus || 'current' },
      set(v) { this.setEmploymentField('employmentStatus', v) }
    },
    employmentEndDate: {
      get() { return this.currentEmployment?.employmentEndDate },
      set(v) { this.setEmploymentField('employmentEndDate', v) }
    },
    contractBasisIndicator: {
      get() { return this.currentEmployment?.contractBasisIndicator ?? false },
      set(v) { this.setEmploymentField('contractBasisIndicator', v) }
    },
    specialRelationshipIndicator: {
      get() { return this.currentEmployment?.specialRelationshipIndicator ?? false },
      set(v) { this.setEmploymentField('specialRelationshipIndicator', v) }
    },
    foreignIncomeIndicator: {
      get() { return this.currentEmployment?.foreignIncomeIndicator ?? false },
      set(v) { this.setEmploymentField('foreignIncomeIndicator', v) }
    },
    virtualCurrencyIndicator: {
      get() { return this.currentEmployment?.virtualCurrencyIndicator ?? false },
      set(v) { this.setEmploymentField('virtualCurrencyIndicator', v) }
    }
  },

  methods: {
    next() {
      if (this.employeeStatus !== Enums.SeEmployeeStatusType.active) {
        this.alertIneligible('Income Calculator currently only supports active employee statuses.')
        return
      }
      if (this.hasIneligibleCurrencyIncome()) {
        this.alertIneligibleCurrencyIncome()
        return
      }
      if (this.specialRelationshipIndicator) {
        this.alertIneligible('Income Calculator cannot currently evaluate income when the borrower is employed by a family member or a party to the transactions, such as the real estate agent or appraiser.')
        return
      }
      if (this.contractBasisIndicator) {
        this.alertIneligible('Income Calculator cannot currently evaluate income when the employment is subject to contractual time limits.')
        return
      }
      if (this.employmentIdx === 0 && this.employmentStartDate && this.getEmploymentMonthsCount < 24) {
        this.alertIneligible('Income Calculator does not currently support employments with less than 24 months of history.')
        return
      }
      wageFormMixin.methods.next.call(this)
    },

    setEmploymentField(field, value) {
      this.$store.commit(SET_WAGE_EMPLOYMENT_DATA, {employmentIdx: this.employmentIdx, fields: {[field]: value}})
    },

    maybeSyncEmploymentStartDate() {
      if (this.employmentIdx !== 0 || !this.currentEmployment) return
      if ((this.currentEmployment.employmentStatus || 'current') !== 'current') return
      let providedDate = this.seRequest?.extraData?.employmentStartDateProvided
      if (providedDate && this.currentEmployment.employmentStartDate !== providedDate) {
        this.setEmploymentField('employmentStartDate', providedDate)
      }
    },

    hasIneligibleCurrencyIncome() {
      return !!this.foreignIncomeIndicator || !!this.virtualCurrencyIndicator
    },

    alertIneligibleCurrencyIncome() {
      let msgHtml = ''
      if (this.virtualCurrencyIndicator) {
        msgHtml += '<p>Income paid to or earned by the borrower in the form of virtual currency, such as cryptocurrencies, is not eligible to be used as qualifying income.</p>'
      }
      if (this.foreignIncomeIndicator) {
        msgHtml += '<p>Income Calculator does not currently evaluate income when the borrower is paid in foreign currency.</p>'
      }
      this.alertHtml(msgHtml)
    },

    alertIneligible(message) {
      this.alertHtml(`<p>${message}</p>`)
    },

    alertHtml(html) {
      Swal.fire({
        html,
        width: 480,
        buttonsStyling: false,
        customClass: {confirmButton: 'bp-button bp-button--primary'},
        confirmButtonText: `OK`,
        icon: 'warning'
      })
    }
  },
});
</script>
<style scoped>
.se-outlined-box {
  border: 1px solid rgba(0, 0, 0, .15);
  border-radius: 4px;
  padding: 12px 16px;
}

.se-toggle-group {
  display: flex;
  gap: 12px;
}

.se-toggle-pill {
  min-width: 100px;
  padding: 8px 24px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, .3);
  background-color: #fff;
  color: #121212;
  font-weight: 600;
}

.se-toggle-pill--active {
  border-color: #1887cc;
  color: #0a649d;
  background-color: #eaf4fb;
}

.se-toggle-pill:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.se-bold-input-text :deep(.el-date-editor .el-input__inner),
.se-bold-input-text :deep(.el-input__inner),
.se-bold-input-text :deep(input.el-input__inner),
.se-bold-input-text :deep(.el-select__selected-item),
.se-bold-input-text :deep(.el-select__placeholder),
.se-bold-input-text :deep(.el-select__placeholder span) {
  color: #000000 !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  -webkit-text-fill-color: #000000 !important;
}
</style>

